import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider, StyledEngineProvider, createTheme, alpha, lighten, getContrastRatio } from '@mui/material/styles';
import colors from 'global/styles/colors';
import { get, set } from 'global/lib/lodash';
import isLoggedIn from 'global/utils/isLoggedIn';
import { red, green, blue } from '@mui/material/colors';
import { mergeDeep } from '../../utils';

const DARK_MODE_MAIN_LIGHTEN = 0.35;
const DARK_MODE_LIGHT_LIGHTEN = 0.5;

const contrastTextForBackground = (background) =>
  getContrastRatio(background, '#fff') >= getContrastRatio(background, '#000') ? '#fff' : '#000';

/** In dark mode, lighten primary/secondary for UI accents; keep original brand color on .dark for nav chrome. */
const brandPaletteForDarkMode = (baseMain) => {
  const main = lighten(baseMain, DARK_MODE_MAIN_LIGHTEN);
  return {
    main,
    light: lighten(baseMain, DARK_MODE_LIGHT_LIGHTEN),
    dark: baseMain,
    contrastText: contrastTextForBackground(main),
  };
};

export const THEMEDEFAULTS = {
  palette: {
    primary: {
      main: colors.performRed,
      light: alpha(colors.performRed, 0.5),
      dark: alpha(colors.performRed, 0.9),
      contrastText: '#fff',
    },
    secondary: {
      main: colors.ibacosBlue,
      light: alpha(colors.ibacosBlue, 0.5),
      dark: alpha(colors.ibacosBlue, 0.9),
      contrastText: '#fff',
    },
    error: red,
    success: green,
    notice: { main: blue[500], light: blue[300], dark: blue[700], contrastText: '#fff' },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1040,
      xl: 1640,
      '2xl': 1920,

      sidebar: 300,
      content: 768,
      ad: 300,
      container: 1400,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          lineHeight: 1.5,
        },
      },
    },
    // Tab labels are title case. Override typography.button's uppercase.
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          zIndex: 900,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          transition: 'none',
        },
      },
    },
    // MUI v6+ removed the `button` prop on ListItem and the implicit
    // `color: inherit; text-decoration: none` it used to apply. When a
    // ListItem/ListItemButton is rendered as <a> via `component={Link}`,
    // the browser's default link styling cascades into ListItemText.
    // Reset it globally so we don't have to patch every styled(ListItem).
    MuiListItem: {
      styleOverrides: {
        root: {
          '&[href], &[role="link"]': {
            color: 'inherit',
            textDecoration: 'none',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&[href], &[role="link"]': {
            color: 'inherit',
            textDecoration: 'none',
          },
        },
      },
    },
  },
  typography: {
    h1: {
      fontSize: '3rem',
    },
    h2: {
      fontSize: '2rem',
    },
    h3: {
      fontSize: '1.5rem',
    },
    h4: {
      fontSize: '1.4rem',
    },
    h5: {
      fontSize: '1.25rem',
    },
  },
};

const newProps = {
  components: {
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary',
      }
    },
    MuiRadio: {
      defaultProps: {
        color: 'secondary',
      }
    },
  },
}

const theme = createTheme(mergeDeep({ ...THEMEDEFAULTS }, newProps));

export default (props) => {
  const [currentTheme, setCurrentTheme] = useState(theme);
  const viewStyle = useSelector((state) => get(state, 'view.style'));
  const auth = useSelector((state) => state.auth);
  const loggedIn = isLoggedIn(auth);

  const buildTheme = (vals) => {
    if (get(vals, 'palette.mode') === 'dark') {
      ['primary', 'secondary', 'notice'].forEach((key) => {
        const baseMain = get(vals, `palette.${key}.main`);
        if (baseMain) {
          set(vals, `palette.${key}`, brandPaletteForDarkMode(baseMain));
        }
      });
    }

    return mergeDeep({ ...vals }, newProps);
  };

  const applyThemeFromViewStyle = () => {
    const newTheme = { ...THEMEDEFAULTS };
    if (get(viewStyle, 'palette.primary.main') && get(viewStyle, 'palette.secondary.main')) {
      delete newTheme.palette.primary;
      delete newTheme.palette.secondary;
      const config = {
        ...newTheme,
        ...viewStyle,
        palette: {
          ...newTheme.palette,
          ...viewStyle.palette,
        },
      };
      setCurrentTheme(createTheme(buildTheme(config)));
    }
  };

  useEffect(() => {
    if (!loggedIn) {
      setCurrentTheme(
        createTheme(
          buildTheme({
            ...THEMEDEFAULTS,
            palette: {
              ...THEMEDEFAULTS.palette,
              mode: get(viewStyle, 'palette.mode', 'light'),
            },
          })
        )
      );
    }
  }, [loggedIn, viewStyle?.palette?.mode]);

  useEffect(() => {
    applyThemeFromViewStyle();
  }, [viewStyle]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={currentTheme}>{props.children}</ThemeProvider>
    </StyledEngineProvider>
  );
};
